import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { map } from "rxjs/operators";
import { AppSettings } from "../../../config/config";
import { apiConstants } from "../../constant/apiConstant";

export type LikeSlug = 'like' | 'love' | 'laugh' | 'celebration' | null;

export interface ActivityMediaItem {
  mediaId: string;
  mediaUrl: string;
  mediaType: 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  mediaSubType: string;
}

export interface ActivityFeedItem {
  activityId: number;
  createdByUserId: number;
  createdByUserName: string;
  createdByBlockedStatus: number;
  createdByUserImage: string;
  createdByUserDesignation: string;
  CreatedOn: string;
  activityLevel: 'SCHOOL' | 'CLASS';
  activityDescription: string;
  LastUpdatedOn: string | null;
  likesCount: number;
  commentsCount: number;
  userLikedStatus: LikeSlug;
  lastLikedByUserName: string | null;
  uniqueFeedLikeTypes: string[];
  media: {
    images: ActivityMediaItem[];
    videos: ActivityMediaItem[];
    documents: ActivityMediaItem[];
  };
  [key: string]: any;
}

export interface GetActivityDataResponse {
  status: boolean;
  body: {
    maxPageNumber: number;
    activities: ActivityFeedItem[];
  };
}

export interface CreateActivityFields {
  activityDescription: string;
  lang: string;
  classId: string;
  staffId: string;
  activityLevel: 'SCHOOL' | 'CLASS';
}

export interface EditActivityFields {
  activityDescription: string;
  lang: string;
  classId: string;
  staffId: string;
  activityId: string;
  deletedMediaIdList?: string;
}

export interface SimpleMessageResponse {
  status: boolean;
  body: {
    message: string;
  };
}

export interface DeleteActivityBody {
  lang: string;
  classId: string;
  staffId: string;
  activityId: string;
  reason: string | null;
}

export interface LikePostBody {
  lang: string;
  classId: string;
  staffId: string;
  activityId: string;
  likeSlug: LikeSlug;
}

export interface LikePostResponse {
  status: boolean;
  body: {
    message: string;
    uniqueLikeTypes: string[];
  };
}

export interface ActivityLikeEntry {
  likedByUserName: string;
  likedByUserImage: string | null;
  likedByUserDesignation: string;
  likeType: 'like' | 'love' | 'laugh' | 'celebration';
  userId: string;
  userType: 'PARENT' | 'STAFF';
  createdByBlockedStatus: number;
}

export interface GetReactionsResponse {
  status: boolean;
  body: {
    activityLikesStat: { like: number; love: number; laugh: number; celebration: number };
    maxPageNumber: number;
    likes: ActivityLikeEntry[];
  };
}

export interface DoCommentBody {
  lang: string;
  classId: string;
  staffId: string;
  activityId: string;
  comment: string;
}

export interface ActivityComment {
  commentId: string;
  comment: string;
  commentByUserId: string;
  [key: string]: any;
}

export interface GetActivityCommentResponse {
  status: boolean;
  body: {
    activityComments: ActivityComment[];
    maxPageNumber: number;
  };
}

export interface DeleteCommentBody {
  lang: string;
  classId: string;
  staffId: string;
  commentId: string;
  reason: string | null;
}

export interface EditCommentBody {
  lang: string;
  classId: string;
  staffId: string;
  comment: string;
  commentId: string;
}

@Injectable({ providedIn: 'root' })
export class StaffActivityApiService {
  private url = AppSettings.API_ENDPOINT_STAFF;

  constructor(private http: HttpClient) {}

  getActivityData(staffId: string, classId: string, pageNumber: number) {
    const params = new HttpParams()
      .set('lang', 'en')
      .set('classId', classId)
      .set('staffId', staffId)
      .set('pageNumber', pageNumber)
      .set('pageLimit', 10);

    return this.http
      .get<GetActivityDataResponse>(`${this.url}${apiConstants.getActivityData}`, { params })
      .pipe(map((res) => res));
  }

  createActivity(fields: CreateActivityFields, mediaFiles: File[]) {
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
    mediaFiles.forEach(file => formData.append('activityMedia', file));

    return this.http
      .post<SimpleMessageResponse>(`${this.url}${apiConstants.createActivity}`, formData)
      .pipe(map((res) => res));
  }

  editActivity(fields: EditActivityFields, mediaFiles: File[]) {
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      if (value !== undefined) { formData.append(key, value); }
    });
    if (mediaFiles.length) {
      mediaFiles.forEach(file => formData.append('activityMedia', file));
    } else {
      formData.append('activityMedia', '[]');
    }

    return this.http
      .put<SimpleMessageResponse>(`${this.url}${apiConstants.editActivity}`, formData)
      .pipe(map((res) => res));
  }

  deleteActivity(body: DeleteActivityBody) {
    return this.http
      .delete<SimpleMessageResponse>(`${this.url}${apiConstants.deleteActivity}`, { body })
      .pipe(map((res) => res));
  }

  likePost(body: LikePostBody) {
    return this.http
      .post<LikePostResponse>(`${this.url}${apiConstants.likeActivityPost}`, body)
      .pipe(map((res) => res));
  }

  getReactions(staffId: string, classId: string, activityId: string, likeType: 'ALL' | LikeSlug, pageNumber: number) {
    const params = new HttpParams()
      .set('lang', 'en')
      .set('likeType', likeType || 'ALL')
      .set('activityId', activityId)
      .set('classId', classId)
      .set('staffId', staffId)
      .set('pageNumber', pageNumber)
      .set('pageLimit', 10);

    return this.http
      .get<GetReactionsResponse>(`${this.url}${apiConstants.getActivityReactions}`, { params })
      .pipe(map((res) => res));
  }

  doComment(body: DoCommentBody) {
    return this.http
      .post<SimpleMessageResponse>(`${this.url}${apiConstants.doActivityComment}`, body)
      .pipe(map((res) => res));
  }

  getActivityComment(staffId: string, classId: string, activityId: string, pageNumber: number, pageLimit: number = 10) {
    const params = new HttpParams()
      .set('staffId', staffId)
      .set('lang', 'en')
      .set('classId', classId)
      .set('activityId', activityId)
      .set('pageNumber', pageNumber)
      .set('pageLimit', pageLimit);

    return this.http
      .get<GetActivityCommentResponse>(`${this.url}${apiConstants.getActivityComment}`, { params })
      .pipe(map((res) => res));
  }

  deleteComment(body: DeleteCommentBody) {
    return this.http
      .delete<{ status: boolean }>(`${this.url}${apiConstants.deleteActivityComment}`, { body })
      .pipe(map((res) => res));
  }

  editActivityComment(body: EditCommentBody) {
    return this.http
      .put<{ status: boolean; body: any }>(`${this.url}${apiConstants.editActivityComment}`, body)
      .pipe(map((res) => res));
  }
}
